<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('leave.edit_leave_type') }}
      </oxd-text>

      <oxd-divider />

      <oxd-form :loading="isLoading" @submitValid="onSave">
        <oxd-form-row>
          <oxd-input-field
            v-model="leaveType.name"
            :label="$t('general.name')"
            :rules="rules.name"
            required
          />
        </oxd-form-row>

        <oxd-form-row>
          <oxd-input-field
            v-model="leaveType.applyFor"
            type="select"
            :label="$t('Apply For')"
            :options="leaveApplyFor"
            :rules="rules.applyFor"
            required
          />
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-group :classes="{wrapper: '--status-grouped-field'}">
                <template #label>
                  <div class="label-is-entitlement-situational">
                    <oxd-label
                      :label="$t('leave.is_entitlement_situational')"
                    />
                    <oxd-icon-button
                      class="--help"
                      name="exclamation-circle"
                      :with-container="false"
                      @click="onModalOpen"
                    />
                  </div>
                </template>
                <oxd-input-field
                  v-model="leaveType.situational"
                  type="radio"
                  :option-label="$t('general.yes')"
                  :value="true"
                />
                <oxd-input-field
                  v-model="leaveType.situational"
                  type="radio"
                  :option-label="$t('general.no')"
                  :value="false"
                />
              </oxd-input-group>
            </oxd-grid-item>

            <oxd-grid-item>
              <oxd-input-group :classes="{wrapper: '--status-grouped-field'}" class="auto-add-entitlement">
                <template #label>
                  <div class="label-is-entitlement-situational">
                    <oxd-label
                      :label="$t('Auto Add Entitlement?')"
                    />
                  </div>
                </template>
                <oxd-input-field
                  v-model="leaveType.autoAddEntitlement"
                  type="radio"
                  :option-label="$t('general.yes')"
                  :value="true"
                />
                <oxd-input-field
                  v-model="leaveType.autoAddEntitlement"
                  type="radio"
                  :option-label="$t('general.no')"
                  :value="false"
                />
              </oxd-input-group>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-input-field
            v-if="leaveType.autoAddEntitlement"
            v-model="leaveType.noDaysEntitlement"
            :label="$t('Entitlement')"
            :rules="rules.noDaysEntitlement"
          />
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions>
          <required-text />
          <oxd-button
            type="button"
            display-type="ghost"
            :label="$t('general.cancel')"
            @click="onCancel"
          />
          <submit-button />
        </oxd-form-actions>
      </oxd-form>
      
      <div class="orangehrm-maintenance-note">
          <div class="orangehrm-maintenance-note-header">
            <oxd-text>Apply For</oxd-text>
          </div>
          <oxd-text>- Remote: Apply for remote working.</oxd-text>
          <oxd-text>- Leave: Apply for normal leave.</oxd-text>
          <oxd-text>- Paid Leave: Apply for paid leave (Select it to apply a separate paid leave calculation formula,
            You should tick "Yes" in <b>Is Auto Add Entitlement</b> field below for its working).</oxd-text>

          <div class="orangehrm-maintenance-note-header">
            <oxd-text>Auto Add Entitlement</oxd-text>
          </div>
          <oxd-text>- Tick "Yes" if you want to automatically add leave days when adding new employees and when entering a new seniority.</oxd-text>

          <div class="orangehrm-maintenance-note-header">
            <oxd-text>Entitlement</oxd-text>
          </div>
          <oxd-text>- If you chosen <b>Is Auto Add Entitlement</b> above, please fill a number of days leave to auto add for employees.</oxd-text>
      </div>
      
      <entitlement-situational-modal
        v-if="showModal"
        @close="onModalClose"
      ></entitlement-situational-modal>
    </div>
  </div>
</template>

<script>
import {navigate} from '@ohrm/core/util/helper/navigation';
import {APIService} from '@ohrm/core/util/services/api.service';
import {
  required,
  shouldNotExceedCharLength,
  greaterThanOrEqual,
} from '@ohrm/core/util/validation/rules';
import Label from '@ohrm/oxd/core/components/Label/Label';
import EntitlementSituationalModal from '@/orangehrmLeavePlugin/components/EntitlementSituationalModal';

const leaveTypeModel = {
  id: '',
  name: '',
  situational: '',
  autoAddEntitlement: false,
  noDaysEntitlement: 0,
  applyFor: '',
};

export default {
  components: {
    'oxd-label': Label,
    'entitlement-situational-modal': EntitlementSituationalModal,
  },
  props: {
    leaveTypeId: {
      type: Number,
      required: true,
    },
    leaveApplyFor: {
      type: Array,
      required: true,
    },
  },

  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/leave/leave-types',
    );
    return {
      http,
    };
  },

  data() {
    return {
      showModal: false,
      isLoading: false,
      leaveType: {...leaveTypeModel},
      rules: {
        name: [required, shouldNotExceedCharLength(50)],
        noDaysEntitlement: [greaterThanOrEqual(1), required],
        applyFor: [required]
      },
    };
  },
  created() {
    this.isLoading = true;
    this.http
      .get(this.leaveTypeId)
      .then(response => {
        const {data} = response.data;
        this.leaveType.id = data.id;
        this.leaveType.name = data.name;
        this.leaveType.situational = data.situational;
        this.leaveType.autoAddEntitlement = data.autoAddEntitlement;
        this.leaveType.noDaysEntitlement = data.noDaysEntitlement;
        this.leaveType.applyFor = this.leaveApplyFor.find(
          item => item.id === data.applyFor,
        );

        // Fetch list data for unique test
        return this.http.getAll();
      })
      .then(response => {
        const {data} = response.data;
        this.rules.name.push(v => {
          const index = data.findIndex(item => item.name == v);
          if (index > -1) {
            const {id} = data[index];
            return id != this.leaveTypeId
              ? this.$t('general.already_exists')
              : true;
          } else {
            return true;
          }
        });
      })
      .finally(() => {
        this.isLoading = false;
      });
  },

  methods: {
    onSave() {
      this.isLoading = true;
      if (!this.leaveType.autoAddEntitlement) {
        this.leaveType.noDaysEntitlement = 0;
      }
      this.http
        .update(this.leaveTypeId, {
          name: this.leaveType.name,
          situational: this.leaveType.situational,
          autoAddEntitlement: this.leaveType.autoAddEntitlement,
          noDaysEntitlement: this.leaveType.noDaysEntitlement,
          applyFor: this.leaveType.applyFor.id,
        })
        .then(() => {
          return this.$toast.updateSuccess();
        })
        .then(() => {
          this.onCancel();
        });
    },
    onCancel() {
      navigate('/leave/leaveTypeList');
    },
    onModalOpen() {
      this.showModal = true;
    },
    onModalClose() {
      this.showModal = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './leave-type.scss';
.orangehrm-maintenance-note {
  display: flex;
  flex-direction: column;
  overflow-wrap: break-word;
  padding: 1.2rem;
  border-radius: 1.2rem;
  color: $oxd-input-control-font-color;
  background-color: $oxd-background-light-gray-color;
  font-size: $oxd-input-control-font-size;

  &-header {
    font-weight: bold;
    margin-top: 0.4rem;
    margin-bottom: 0.3rem;
  }
}
</style>
